import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Play, Pause, ArrowLeft, Volume2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/sonic`;

const SonicPlayer = () => {
  const { id } = useParams();
  const [artifact, setArtifact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchArtifact();
  }, [id]);

  const fetchArtifact = async () => {
    try {
      const response = await axios.get(`${API}/artifacts/${id}`);
      setArtifact(response.data);
    } catch (error) {
      console.error('Error fetching artifact:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <div 
          className="text-copper text-3xl animate-pulse"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}
        >
          Loading artifact...
        </div>
      </div>
    );
  }

  if (!artifact) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="text-center">
          <h2 
            className="text-3xl font-bold text-off-white mb-4"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Artifact not found
          </h2>
          <Link
            to="/sonic"
            className="inline-block px-6 py-3 bg-copper hover:bg-copper-dark text-charcoal font-bold rounded-lg transition-all"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Back to Gallery
          </Link>
        </div>
      </div>
    );
  }

  const qrUrl = `${window.location.origin}/sonic/play/${artifact.id}`;
  const audioUrl = `${BACKEND_URL}/api/sonic/audio/${artifact.id}`;

  return (
    <div className="min-h-screen bg-charcoal py-20 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link
          to="/sonic"
          className="inline-flex items-center gap-2 text-copper hover:text-copper-dark mb-8 transition-colors"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-semibold">Back to Gallery</span>
        </Link>

        {/* Main Player Card */}
        <div className="bg-charcoal border-2 border-copper rounded-xl p-8 md:p-12 shadow-2xl shadow-copper/10">
          {/* Title */}
          <h1 
            className="text-4xl md:text-5xl font-black text-copper mb-4"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            {artifact.title}
          </h1>
          
          {artifact.description && (
            <p 
              className="text-off-white/80 text-lg mb-8"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              {artifact.description}
            </p>
          )}

          {/* Audio Element (Hidden) */}
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
            preload="metadata"
          />

          {/* Custom Audio Player UI */}
          <div className="mb-8">
            {/* Waveform Visual (Animated Bars) */}
            <div className="mb-6 h-32 bg-charcoal border-2 border-copper/30 rounded-lg p-4 flex items-center justify-center gap-1">
              {[...Array(50)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-copper rounded-full transition-all duration-200"
                  style={{
                    height: playing 
                      ? `${30 + Math.sin((currentTime * 10 + i) / 3) * 50}%`
                      : '30%',
                    opacity: playing ? 0.8 : 0.4
                  }}
                />
              ))}
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div 
                className="h-2 bg-copper/20 rounded-full cursor-pointer overflow-hidden"
                onClick={handleSeek}
              >
                <div 
                  className="h-full bg-copper rounded-full transition-all"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>
              <div 
                className="flex justify-between mt-2 text-sm text-off-white/60"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
            
            {/* Play Button */}
            <div className="flex justify-center items-center gap-4">
              <Volume2 className="h-6 w-6 text-copper" />
              <button
                onClick={togglePlay}
                className="w-20 h-20 rounded-full bg-copper hover:bg-copper-dark flex items-center justify-center transition-all transform hover:scale-110 shadow-lg shadow-copper/30"
              >
                {playing ? (
                  <Pause className="h-10 w-10 text-charcoal" />
                ) : (
                  <Play className="h-10 w-10 text-charcoal ml-1" />
                )}
              </button>
              <Volume2 className="h-6 w-6 text-copper" />
            </div>
          </div>

          {/* QR Code Section */}
          <div className="border-t-2 border-copper/20 pt-8 mt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex-1">
                <h3 
                  className="text-2xl font-bold text-off-white mb-3"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}
                >
                  SCAN TO PLAY
                </h3>
                <p 
                  className="text-off-white/70"
                  style={{ fontFamily: 'Rajdhani, sans-serif' }}
                >
                  Share this artifact by scanning the QR code with any device
                </p>
              </div>
              <div className="bg-off-white p-4 rounded-lg">
                <QRCodeSVG value={qrUrl} size={150} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SonicPlayer;